import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Button, Link, Slide, useMediaQuery } from '@mui/material';
import {
  acceptAll,
  rejectNonEssential,
  applyStoredConsent,
  hasConsentChoice,
  CONSENT_REOPEN_EVENT,
} from '../utils/consent';

/**
 * First-party cookie-consent banner wired to Google Consent Mode v2.
 *
 * - On mount: re-applies a returning visitor's stored grant (index.html resets
 *   Consent Mode to "denied" each load), then shows itself only if no choice
 *   has been made.
 * - "Accept all" grants analytics + advertising; "Reject non-essential" keeps
 *   both denied. Both buttons carry equal prominence (UK GDPR: rejecting must
 *   be as easy as accepting).
 * - Re-openable via the footer "Cookie settings" link (CONSENT_REOPEN_EVENT).
 *
 * Mounted once in App's Layout, outside Suspense, so it appears regardless of
 * which lazy route is loading.
 *
 * It also publishes its own height as the CSS custom property
 * `--consent-banner-h` on <html>, and clears it to 0px as soon as a choice is
 * made. This banner is fixed to the bottom of the viewport at z-index 1401, so
 * anything else living there — today the planner's mobile bottom bar — is
 * covered until it is answered. Rather than have each of those hard-code a
 * guess at the banner's height, the banner states it and they offset by it.
 */
export const CONSENT_BANNER_HEIGHT_VAR = '--consent-banner-h';

const ConsentBanner = () => {
  const [open, setOpen] = useState(false);
  const bannerRef = useRef(null);
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  // Publish / retract the height. Runs on every `open` change, and re-measures
  // while open — the banner wraps to three lines at 390 and reflows on rotate,
  // so a single measurement at mount goes stale.
  useEffect(() => {
    const root = document.documentElement;
    const clear = () => root.style.setProperty(CONSENT_BANNER_HEIGHT_VAR, '0px');
    if (!open) {
      clear();
      return undefined;
    }
    const measure = () => {
      const h = bannerRef.current?.getBoundingClientRect().height || 0;
      root.style.setProperty(CONSENT_BANNER_HEIGHT_VAR, `${Math.round(h)}px`);
    };
    measure();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && bannerRef.current) {
      ro = new ResizeObserver(measure);
      ro.observe(bannerRef.current);
    }
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
      clear();
    };
  }, [open]);

  useEffect(() => {
    applyStoredConsent();
    if (!hasConsentChoice()) setOpen(true);

    const reopen = () => setOpen(true);
    window.addEventListener(CONSENT_REOPEN_EVENT, reopen);
    return () => window.removeEventListener(CONSENT_REOPEN_EVENT, reopen);
  }, []);

  const handleAccept = () => {
    acceptAll();
    setOpen(false);
  };
  const handleReject = () => {
    rejectNonEssential();
    setOpen(false);
  };

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit timeout={reduceMotion ? 0 : undefined}>
      <Box
        ref={bannerRef}
        role="region"
        aria-label="Cookie consent"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (t) => t.zIndex.snackbar + 1,
          bgcolor: 'secondary.main',
          color: '#FFFFFF',
          borderTop: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 -8px 32px rgba(20,33,61,0.28)',
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 2.5 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { md: 'center' },
              gap: { xs: 2, md: 4 },
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.82)', lineHeight: 1.7, flex: 1, maxWidth: '70ch' }}
            >
              We use cookies to measure how the site is used and, in future, to support advertising.
              Essential cookies always run. Read our{' '}
              <Link component={RouterLink} to="/cookies" sx={{ color: '#FFFFFF', textDecoration: 'underline' }}>
                Cookie Policy
              </Link>{' '}
              and{' '}
              <Link component={RouterLink} to="/privacy" sx={{ color: '#FFFFFF', textDecoration: 'underline' }}>
                Privacy Policy
              </Link>
              .
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
              <Button
                onClick={handleReject}
                variant="outlined"
                sx={{
                  color: '#FFFFFF',
                  borderColor: 'rgba(255,255,255,0.4)',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  '&:hover': { borderColor: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' },
                  '&:active': { transform: 'scale(0.97)' },
                }}
              >
                Reject non-essential
              </Button>
              <Button
                onClick={handleAccept}
                variant="contained"
                sx={{
                  bgcolor: 'primary.main',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: '#A50D26' },
                  '&:active': { transform: 'scale(0.97)' },
                }}
              >
                Accept all
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Slide>
  );
};

export default ConsentBanner;
